import type {
  StorefrontQuote,
  StorefrontQuoteRequest,
} from "@emynor/storefront-sdk";
import type { Cart, CartLine } from "./types";
import { cartCanCommitAfterQuoteError } from "./customer-error";
import { browserUuid } from "./browser-ids";

export function calculateLineTotal(line: CartLine): number {
  return (
    (line.unitPrice +
      line.additions.reduce((sum, addition) => sum + addition.price, 0)) *
    line.quantity
  );
}

/** Keep persisted cart estimates in step with the server's authoritative quote. */
export function cartWithQuotePrices(cart: Cart, quote: StorefrontQuote): Cart {
  if (cart.lines.length !== quote.lines.length) return cart;
  const pricedLines = quote.lines.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  if (
    cart.lines.some((line, index) => {
      const priced = pricedLines[index];
      return (
        !priced ||
        priced.sortOrder !== index ||
        priced.itemId !== line.itemId ||
        priced.variantId !== (line.variationId ?? null) ||
        priced.quantity !== line.quantity ||
        priced.addons.length !== line.additions.length ||
        line.additions.some((addon) => !priced.addons.some((entry) => entry.id === addon.id))
      );
    })
  ) return cart;
  let changed = false;
  const lines = cart.lines.map((line, index) => {
    const priced = pricedLines[index]!;
    const additions = line.additions.map((addon) => {
      const price = priced.addons.find((entry) => entry.id === addon.id)!.priceMinor;
      if (price !== addon.price) changed = true;
      return price === addon.price ? addon : { ...addon, price };
    });
    const originalUnitPrice = priced.originalUnitPriceMinor ?? null;
    if (line.unitPrice !== priced.baseUnitPriceMinor) changed = true;
    if ((line.originalUnitPrice ?? null) !== originalUnitPrice) changed = true;
    return line.unitPrice === priced.baseUnitPriceMinor &&
      (line.originalUnitPrice ?? null) === originalUnitPrice &&
      additions.every((addon, i) => addon === line.additions[i])
      ? line
      : { ...line, unitPrice: priced.baseUnitPriceMinor, originalUnitPrice, additions };
  });
  return changed ? { ...cart, lines } : cart;
}

export function buildQuoteInput(cart: Cart): StorefrontQuoteRequest | null {
  const orderType = cart.orderType;
  if (!orderType || cart.lines.length === 0) return null;
  return {
    branchId: orderType.branchId,
    orderType: orderType.type,
    lines: cart.lines.map((line) => ({
      itemId: line.itemId,
      ...(line.variationId ? { variantId: line.variationId } : {}),
      addonIds: line.additions.map((addition) => addition.id),
      quantity: line.quantity,
      ...(line.note ? { notes: line.note } : {}),
    })),
    ...(cart.promotionCode ? { promotionCode: cart.promotionCode } : {}),
    ...(cart.redeemLoyaltyPoints ? { redeemLoyaltyPoints: cart.redeemLoyaltyPoints } : {}),
    ...(linkedAttribution(cart)
      ? { promotionAttributionToken: linkedAttribution(cart)!.token }
      : {}),
    ...(orderType.type === "delivery" &&
    (orderType.deliveryLocation || orderType.deliveryAddress)
      ? {
          location: orderType.deliveryLocation ?? {
            lat: orderType.deliveryAddress!.lat,
            lng: orderType.deliveryAddress!.lng,
          },
        }
      : {}),
    fulfillment: orderType.fulfillment ?? { mode: "asap" },
  };
}

function linkedAttribution(cart: Cart) {
  return (
    cart.linkedPromotionAttribution ??
    (cart.promotionAttribution?.source === "link"
      ? cart.promotionAttribution
      : undefined)
  );
}

export function quoteInputFingerprint(cart: Cart): string | null {
  const input = buildQuoteInput(cart);
  return input ? JSON.stringify(input) : null;
}

export async function validateCartAddition(
  current: Cart,
  line: Omit<CartLine, "id">,
  quoteCart: (input: StorefrontQuoteRequest) => Promise<StorefrontQuote>,
  createId: () => string = browserUuid,
): Promise<{
  cart: Cart;
  quote: StorefrontQuote | null;
  quoteError: unknown | null;
}> {
  const cart = cartWithLine(current, line, createId);
  const input = buildQuoteInput(cart);
  if (!input) throw new Error("Choose an order type before adding an item");

  try {
    return { cart, quote: await quoteCart(input), quoteError: null };
  } catch (error) {
    if (!cartCanCommitAfterQuoteError(error)) throw error;
    return { cart, quote: null, quoteError: error };
  }
}

function cartWithLine(
  current: Cart,
  line: Omit<CartLine, "id">,
  createId: () => string,
): Cart {
  const signature = (candidate: Omit<CartLine, "id">) =>
    [
      candidate.itemId,
      candidate.variationId ?? "",
      candidate.note ?? "",
      candidate.additions
        .map((addition) => addition.id)
        .sort()
        .join("|"),
    ].join("::");
  const existing = current.lines.find(
    (candidate) => signature(candidate) === signature(line),
  );
  if (existing) {
    return {
      ...current,
      lines: current.lines.map((candidate) =>
        candidate.id === existing.id
          ? { ...candidate, quantity: candidate.quantity + line.quantity }
          : candidate,
      ),
    };
  }
  return {
    ...current,
    lines: [...current.lines, { ...line, id: createId() }],
  };
}
